import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/** 详情页多卡片预览：同时仅允许一路播放；由 Provider 持有当前 `playingId` */
export interface FJDetailsPlaybackContextValue {
    playingId: string | null;
    /** 某片段即将开始播放时调用，其它片段会通过 effect 收到「非本人」而 pause */
    requestPlay: (id: string) => void;
    /** 某片段暂停结束时调用（含用户暂停、播完回调），仅当仍是当前 playingId 时清空 */
    notifyPaused: (id: string) => void;
    /** 侧栏切换片段等场景：清空独占播放权，驱动其它卡片上的播放器 pause */
    clearPlayback: () => void;
    /** 各卡片注册 pause 回调，供多选等场景真正暂停底层媒体 */
    registerPauseHandler: (id: string, pause: () => void) => () => void;
    /** 暂停当前正在播放的卡片并清空 playingId */
    pauseActivePlayback: () => void;
}

export const FJDetailsPlaybackContext = createContext<FJDetailsPlaybackContextValue | null>(null);

export function FJDetailsPlaybackProvider({ children }: { children: ReactNode }) {
    const [playingId, setPlayingId] = useState<string | null>(null);
    const playingIdRef = useRef<string | null>(null);
    const pauseHandlersRef = useRef(new Map<string, () => void>());

    useEffect(() => {
        playingIdRef.current = playingId;
    }, [playingId]);

    const requestPlay = useCallback((id: string) => {
        setPlayingId(id);
    }, []);

    const notifyPaused = useCallback((id: string) => {
        setPlayingId(prev => (prev === id ? null : prev));
    }, []);

    const clearPlayback = useCallback(() => {
        setPlayingId(null);
    }, []);

    const registerPauseHandler = useCallback((id: string, pause: () => void) => {
        pauseHandlersRef.current.set(id, pause);
        return () => {
            pauseHandlersRef.current.delete(id);
        };
    }, []);

    const pauseActivePlayback = useCallback(() => {
        const id = playingIdRef.current;
        if (id) {
            pauseHandlersRef.current.get(id)?.();
        }
        setPlayingId(null);
    }, []);

    const value = useMemo(
        () => ({
            playingId,
            requestPlay,
            notifyPaused,
            clearPlayback,
            registerPauseHandler,
            pauseActivePlayback,
        }),
        [playingId, requestPlay, notifyPaused, clearPlayback, registerPauseHandler, pauseActivePlayback],
    );

    return <FJDetailsPlaybackContext.Provider value={value}>{children}</FJDetailsPlaybackContext.Provider>;
}
